<template>
  <a
    v-if="loyaltyStatusAndLinks.enabled"
    :href="loyalty.optedIn ? dashboardPage : landingPage"
    :class="[$style['enjoy-rewards'], { [$style.mobile]: mobile }]"
    @click="publishEventAndOpen"
  >
    <span>{{ translations.enjoyLoyalty }}</span>
    <span
      v-if="isInUserMarket && loyalty.optedIn && loyalty.wallet.available"
      :class="$style['enjoy-rewards-available-balance']"
    >
      {{ loyalty.wallet.available }}
    </span>
  </a>
</template>

<script>
import { CurrencyService } from "@nuskin/ns-shop";
import { UserService } from "@nuskin/ns-account";
import webLoyalty from "@nuskin/ns-loyalty-web";
import { events } from "@nuskin/ns-util";

import vueEventBus from "./EventBus";

export default {
  name: "NsHeaderLoyaltyRewards",
  props: {
    mobile: Boolean,
    translations: {
      type: Object,
      default: function() {
        return {
          enjoyLoyalty: "Enjoy Rewards"
        };
      }
    }
  },
  data() {
    return {
      isInUserMarket: false,
      dashboardPage: "",
      landingPage: "",
      /**
       * Object that holds the rewards status and links
       */
      loyaltyStatusAndLinks: {
        enabled: false,
        landingPage: "",
        dashboardPage: ""
      },
      /**
       * Object that holds the rewards totals for the current user
       */
      loyalty: {
        optedIn: false,
        wallet: {}
      }
    };
  },
  computed: {
    runConfig() {
      return this.$NsAppService.runConfig || {};
    },
    marketConfig() {
      return this.$NsAppService.marketConfig || {};
    },
    locale() {
      return this.$NsAppService.locale || "";
    }
  },
  mounted() {
    this.addListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    init() {
      const user = UserService.getUser();

      this.isInUserMarket = !!(
        user && user.countryCd === this.runConfig.country
      );

      this.loadRewardsData();
    },

    formatWalletValuesToLocalCurrency(wallet) {
      const currency = CurrencyService.getCurrency(this.runConfig.country);
      const k = Math.pow(10, currency.accuracy);
      const truncatedPoints = Math.trunc(wallet.available * k) / k;
      const returnWallet = {
        available: CurrencyService.format(
          truncatedPoints,
          this.runConfig.country
        )
      };
      return returnWallet;
    },

    localeReplace(str) {
      return (str || "").replace("{locale}", this.locale);
    },

    loadRewardsData() {
      // First we need to see if we have the info in the session:
      this.loyaltyStatusAndLinks =
        this.marketConfig.loyalty || this.loyaltyStatusAndLinks;

      if (this.loyaltyStatusAndLinks.enabled) {
        this.dashboardPage = this.localeReplace(
          this.loyaltyStatusAndLinks.dashboardPage
        );
        this.landingPage = this.localeReplace(
          this.loyaltyStatusAndLinks.landingPage
        );

        this.checkLoyaltyValues();
      }

      events.subscribe(events.loyalty.LOYALTY_DATA_UPDATED, () => {
        //publishing that the event has happened via eventbus because this component is used multiple times on the same page
        // for some reason the jquery events is losing the subscription for some instances.
        vueEventBus.$emit(events.loyalty.LOYALTY_DATA_UPDATED);
      });
    },

    publishEventAndOpen(ev) {
      ev.preventDefault();

      events.publish(events.loyalty.LOYALTY_REWARDS_HEADER_BUTTON, {
        event: events.loyalty.LOYALTY_REWARDS_HEADER_BUTTON,
        context: "header"
      });

      // Open page, emmulate the default behavior.
      let link = ev.target.closest("a");
      if (link) {
        let href = link.href;
        let useMeta = /Mac/i.test(navigator.platform);
        let target =
          (!useMeta && ev.ctrlKey) || (useMeta && ev.metaKey)
            ? "_blank"
            : "_self";

        window.open(href, target);
      }
    },

    checkLoyaltyValues() {
      if (this.loyaltyStatusAndLinks.enabled) {
        const loyaltyData = webLoyalty.getLoyaltyRewardsPoints();
        if (loyaltyData && loyaltyData.optInStatus) {
          this.setLoyaltyValues(loyaltyData);
        }
      }
    },

    setLoyaltyValues(loyaltyData) {
      const loyaltyInfo = {};
      loyaltyInfo.optedIn = true;
      loyaltyInfo.wallet = this.formatWalletValuesToLocalCurrency(
        loyaltyData.wallet
      );
      this.loyalty = loyaltyInfo;
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // watch app service to finish loading
      this.$watch(
        function() {
          return !this.$NsAppService.loading;
        },
        this.init,
        {
          immediate: true
        }
      );

      // listen to config changes
      vueEventBus.$on("config-changed", this.init);

      // listen to loyalty changes
      vueEventBus.$on(
        events.loyalty.LOYALTY_DATA_UPDATED,
        this.checkLoyaltyValues
      );
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to event bus
      vueEventBus.$off();
    }
  }
};
</script>

<style module lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

a.enjoy-rewards {
  text-decoration: none;
  color: map.get(colors.$themeColors, color-primary-text);
  cursor: pointer;
  border-bottom: 1px solid transparent;
  font-size: 14px;

  &.mobile {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    border: none;
    width: 100%;
    padding: 9px 15px 9px 29px;
    color: map.get(colors.$themeColors, color-interactive-8);
    min-height: 40px;
    font-size: 18px;
    font-weight: 300;
    line-height: normal;

    &:hover {
      border: none;
    }
  }

  &:hover {
    text-decoration: none;
    border-bottom: 1px solid currentColor;
  }
}

.enjoy-rewards-available-balance {
  background-color: #272525;
  color: white;
  padding: 0px 9px;
  border-radius: 20px;
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  font-size: map.get(
    typographyVariables.$typography,
    subtitle-small-bold-caps_-_font-size
  );
  margin-left: 10px;
}
</style>
